<template>
  <div>
    <base-card :loading="loading" class="rounded-lg pb-2">
      <div
        class="d-flex justify-center text--disabled align-center gap-1 pt-1 unselectable"
      >
        <small class="lh-1">
          {{ organization?.name || "Carregando..." }}
        </small>
      </div>
      <v-tabs v-model="tab" show-arrows class="rounded-lg" height="36px">
        <v-tab
          v-for="tab in loading ? [] : tabsFiltered"
          class="rounded-lg"
          :key="tab.component"
        >
          <v-icon left small>{{ tab.icon }}</v-icon>
          {{ tab.name }}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" :touchless="tabsFiltered[tab]?.touchless">
        <v-tab-item
          v-for="item in loading ? [] : tabsFiltered"
          :key="item.name"
          class="pa-4"
        >
          <component
            :is="item.component"
            :organization="organization"
            @refresh="loadOrganization"
          />
        </v-tab-item>
      </v-tabs-items>
    </base-card>

    <!-- Navigation -->
    <v-bottom-sheet
      v-model="navigationSheet"
      inset
      max-width="550px"
      :overlay-opacity="0.8"
      content-class="elevation-0"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          fab
          dark
          style="position: fixed; bottom: 76px; right: 16px; z-index: 5"
          float
          v-bind="attrs"
          v-on="on"
          color="primary"
        >
          <v-icon>mdi-dots-grid</v-icon>
        </v-btn>
      </template>
      <div class="mb-4 px-4 d-flex">
        <v-card class="pa-3 pb-1 rounded-lg">
          <v-row dense class="mx-0">
            <v-col
              v-for="tab in tabsFiltered"
              :key="tab?.id"
              cols="4"
              sm="4"
              md="3"
            >
              <v-card
                @click="selectTab(tab?.id)"
                outlined
                height="90px"
                elevation="0"
                width="300px"
                class="pa-2"
              >
                <div
                  class="d-flex align-center justify-center flex-column h-full text-center lh-1 gap-2"
                >
                  <v-icon>{{ tab.icon }}</v-icon>
                  {{ tab.name }}
                </div>
              </v-card>
            </v-col>
          </v-row>
          <div class="d-flex justify-center">
            <v-btn @click="navigationSheet = false" text small>Fechar</v-btn>
          </div>
        </v-card>
      </div>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import organizationsService from "@/services/admin/organizations";
import OrganizationDetails from "@/components/admin/organization/OrganizationDetails.vue";
import OrganizationBankAccounts from "@/components/admin/organization/OrganizationBankAccounts.vue";
import OrganizationSettings from "@/components/admin/organization/OrganizationSettings.vue";

export default {
  name: "OrganizationManagement",
  metaInfo() {
    return { title: `⚙️ ${this.organization?.name || "Carregando..."}` };
  },

  data: () => ({
    loading: true,
    organization: null,
    tab: null,
    navigationSheet: false,
    tabs: [
      {
        id: "details",
        name: "Detalhes",
        component: "OrganizationDetails",
        permission: null,
        icon: "mdi-information",
        touchless: true,
      },
      {
        id: "bank-accounts",
        name: "Contas Bancárias",
        component: "OrganizationBankAccounts",
        permission: null,
        icon: "mdi-bank",
        touchless: true,
      },
      // {
      //   id: "settings",
      //   name: "Configurações",
      //   component: "OrganizationSettings",
      //   permission: null,
      //   icon: "mdi-cog",
      //   touchless: true,
      // },
    ],
  }),

  methods: {
    getInitialTab() {
      const hash = window.location.hash.toLowerCase();
      const tabIndex = this.tabs.findIndex((tab) => `#${tab.id}` === hash);
      return tabIndex >= 0 ? tabIndex : 0;
    },

    async loadOrganization() {
      this.loading = true;
      try {
        const organizationId = this.$route.params.id;
        const response = await organizationsService.getOrganizationById(
          organizationId
        );
        this.organization = {
          ...response.organization,
          commercial_representative: response.organization
            .CommercialResponsible || {
            phone: "",
            ddi: 55,
          },
        };
      } catch (error) {
        console.error("Erro ao carregar organização:", error);
      } finally {
        this.loading = false;
      }
    },

    handleCoverPreview(file) {
      if (!file) {
        this.previewCover = null;
        return;
      }
      this.previewCover = URL.createObjectURL(file);
    },

    async handleUpdateOrganization() {
      try {
        this.loading = true;
        const organizationId = this.$route.params.id;

        let commercial_representative =
          this.organization.commercial_representative;
        if (typeof commercial_representative === "string") {
          commercial_representative = {
            phone: commercial_representative,
            ddi: this.organization.ddi || 55,
          };
        }

        if (this.coverFile) {
          const formData = new FormData();
          formData.append("file", this.coverFile);

          const response =
            await organizationsService.uploadOrganizationCoverPhoto(
              organizationId,
              formData
            );

          if (!response.success) {
            throw new Error(
              response.message || "Erro ao fazer upload da imagem"
            );
          }

          this.organization.cover = response.coverUrl;
        }

        const updateData = {
          name: this.organization.name,
          slug: this.organization.slug,
          email: this.organization.email,
          phone: this.organization.phone,
          commercial_representative,
          description: this.organization.description,
        };

        await organizationsService.updateOrganization(
          organizationId,
          updateData
        );

        this.coverFile = null;
        this.previewCover = null;
        await this.loadOrganization();
      } catch (error) {
        console.error("Erro ao atualizar organização:", error);
        this.$toast.error(error.error || "Erro ao atualizar organização");
      } finally {
        this.loading = false;
      }
    },

    selectTab(tab) {
      if (tab) window.location.replace("#" + tab);
      if (this.$route.hash) {
        let hash = tab || this.$route.hash.replace("#", "");
        let index = this.tabsFiltered.findIndex((tab) => tab?.id == hash);
        if (index >= 0) this.tab = index;
        this.navigationSheet = false;
      }
    },

    vibrate() {
      if (navigator.vibrate) navigator.vibrate(50);
    },
  },

  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    ...mapGetters("company", ["selectedCompany"]),
    tabsFiltered() {
      return this.tabs.filter(
        (tab) => !tab.permission || this.hasPermission(tab.permission)
      );
    },
  },

  watch: {
    "selectedCompany.id"() {
      this.$router.push({ name: "admin.organizations" });
    },
    tab: {
      handler(val, oldVal) {
        if (val !== null && oldVal !== null) this.vibrate();
        let tab = this.tabsFiltered[val];
        if (tab) window.location.replace("#" + tab?.id);
      },
      immediate: true,
    },
    "$route.hash"() {
      this.selectTab();
    },
  },

  components: {
    OrganizationDetails,
    OrganizationBankAccounts,
    OrganizationSettings,
  },

  mounted() {
    this.loadOrganization();
    this.tab = this.getInitialTab();
  },
};
</script>
